import React, { useMemo, useRef } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import {
  CodeCell,
  IndexColumn,
  SourceColumn,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateTime } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { Settlement } from "features/EmployeeAdvanceSettlements/types/employee-advance-settlement.types";
import { getEmployeeAdvanceSettlements } from "features/EmployeeAdvanceSettlements/services/EmployeeAdvanceSettlementService";
import { EmployeeAdvanceSettlementSourceTypeLabel } from "features/EmployeeAdvanceSettlements/constants/employee-advance-settlement.constants";
import {
  resolveEmployeeAdvanceSettlementSourceCode,
  resolveEmployeeAdvanceSettlementSourceLinkId,
} from "features/EmployeeAdvanceSettlements/utils/employeeAdvanceSettlementSource";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

export const EmployeeAdvanceSettlementTable = () => {
  const { can } = useAccess();
  const canReadAdvance = can(Permissions.EMPLOYEE_ADVANCE.READ);
  const tableRef = useRef<() => void>(null);

  const columns: ColumnDef<Settlement>[] = useMemo(
    () => [
      IndexColumn<Settlement>(),
      {
        header: "Code",
        cell: ({ row }) => <CodeCell code={row.original.code} />,
        accessorKey: "code",
        enableColumnFilter: false,
      },
      {
        header: "Employee Advance",
        accessorKey: "employeeAdvance.code",
        enableColumnFilter: false,
        cell: ({ row }) =>
          canReadAdvance ? (
            <Link
              to={`/finance/employee-advances/${row.original.employeeAdvanceId}`}
            >
              {row.original.employeeAdvance?.code ??
                `#${row.original.employeeAdvanceId}`}
            </Link>
          ) : (
            (row.original.employeeAdvance?.code ??
            `#${row.original.employeeAdvanceId}`)
          ),
      },
      SourceColumn<Settlement>({
        resolve: (row) =>
          resolveMappedSourceCell(
            row.sourceType,
            resolveEmployeeAdvanceSettlementSourceLinkId(row.sourceType, row.sourceId, row.source),
            {
              typeLabelMap: EmployeeAdvanceSettlementSourceTypeLabel,
              source: {
                code: resolveEmployeeAdvanceSettlementSourceCode(row.sourceType, row.source),
              },
            },
          ),
      }),
      {
        header: "Amount",
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => formatCurrency(row.original.amount),
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => <DateTime date={row.original.created_at} />,
      },
    ],
    [canReadAdvance],
  );

  return (
    <React.Fragment>
      <TableContainer
        columns={columns}
        fetchApi={getEmployeeAdvanceSettlements}
        pageSize={10}
        globalSearch={true}
        refreshRef={tableRef}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
      />
    </React.Fragment>
  );
};
